import type { IstoreItem } from '@typings/store/storeItem.interface';
import { IEditHistoryEntry } from '@typings/editHistory.interface';

// 미션 타입 정의
export enum QuestType {
  MAIN = 'main',
  SUB = 'sub',
  MEMBER_REQUEST = 'member_request',
  USER = 'user',
}

export enum QuestStatus {
  NOT_STARTED = 'not_started',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export enum RewardType {
  ITEM = 'item', // 특정 아이템 지급 (복수 선택 가능)
  STAT = 'stat', // 특정 스탯 수치 올리기 (ex: mp+1)
  CURRENCY = 'currency', // 재화 지급
  TITLE = 'title', // 타이틀 지급
  EXPERIENCE = 'experience', // (하위 호환용) 경험치 - STAT으로 대체 권장
  EXPLORATION_POINT = 'exploration_point', // 신규: 탐색 포인트 (양수: 지급, 음수: 차감)
}

// 스탯 보상 상세 정보
export interface IStatReward {
  statId: string; // 스탯 ID
  amount: number; // 올릴 수치 (양수: 증가, 음수: 감소)
}

// 퀘스트 보상 인터페이스
export interface IQuestReward {
  type: RewardType;
  name?: string; // 보상 이름 (선택)
  amount?: number; // 수량 (재화, 아이템 개수 등)
  icon?: string; // 아이콘 (선택)
  // 아이템 보상
  itemId?: string; // 단일 아이템 ID (하위호환용)
  itemIds?: string[]; // 다수 아이템 ID 배열
  itemData?: Partial<IstoreItem>; // StoreItem 스냅샷(이미지/제목/설명 등)
  // 스탯 보상
  statRewards?: IStatReward[]; // 스탯 보상 목록 (복수 스탯 가능)
  // 타이틀 보상
  titleId?: string; // 타이틀 ID
  description?: string;
}

// 퀘스트 요청자 정보 인터페이스
export interface IQuestRequester {
  key: string; // 캐릭터 ID
  path: string; // 컬렉션 경로 (photocard 또는 photocard2)
  thumbnail?: string; // 캐릭터 썸네일
  value?: string; // 캐릭터 이름
}

// 퀘스트 참여자 상태
export enum QuestMemberStatus {
  ACCEPTED = 'accepted', // 수락 (진행 중)
  ABANDONED = 'abandoned', // 포기
  COMPLETED = 'completed', // 완료
}

export const QuestMemberStatusMap: Record<QuestMemberStatus, string> = {
  [QuestMemberStatus.ACCEPTED]: '진행 중',
  [QuestMemberStatus.ABANDONED]: '포기',
  [QuestMemberStatus.COMPLETED]: '완료',
};

// 퀘스트 참여자(수락한 멤버) 정보 인터페이스
export interface IQuestParticipant {
  odId: string; // 유저 UID (통합 필드)
  characterId?: string; // 캐릭터 ID (선택)
  // 상태 관리
  status?: QuestMemberStatus; // 상태 (기본: ACCEPTED)
  joinedAt: number; // 수락 시간 (timestamp)
  updatedAt?: number; // 마지막 업데이트 시간 (timestamp)
  note?: string; // 관리자 메모
}

// 레거시 데이터 호환을 위한 유틸리티 함수
export function getParticipantId(p: IQuestParticipant | { userId?: string; odId?: string }): string {
  return (p as any).odId || (p as any).userId || '';
}

// 퀘스트 기본 정보 인터페이스
export interface IQuest {
  id: string;
  type: QuestType;
  title: string;
  subTitle?: string;
  summary?: string;
  description: string;
  location?: string;
  requirements?: string[];
  rewards: IQuestReward[];
  difficulty: number; // 1-5 (별점)
  priority: number;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string; // 관리자 또는 사용자 ID
  isActive: boolean;
  bgImgURL?: string; // 사용자 설정 배경 이미지 URL
  hoverBgImgURL?: string; // 사용자 설정 호버 배경 이미지 URL
  borderImgURL?: string; // 사용자 설정 보더 이미지 URL
  iconURL?: string; // 퀘스트 아이콘 이미지 URL (정사각형 권장)
  isShowTitle?: boolean; // 타이틀 표시 여부
  acceptCondition?: IQuestAcceptCondition; // 퀘스트 수락(진행) 조건
  completionCondition?: IQuestCompletionCondition; // 레거시 단일 완료 조건 (하위 호환)
  completionConditions?: IQuestCompletionCondition[]; // 복수 완료 조건 (AND 로직)
  requestedBy?: IQuestRequester; // 퀘스트 요청자 (캐릭터 정보)
  participants?: IQuestParticipant[]; // 퀘스트를 수락한 멤버 목록
  isRandomReward?: boolean; // 랜덤 보상 지급 여부 (true: 보상 중 하나만 랜덤 지급)
  repeatSchedule?: IQuestRepeatSchedule; // 반복 조건 (요일 반복)
  lastEditedBy?: string;
  editHistory?: IEditHistoryEntry[];
  // ── 확률 시스템 ─────────────────────────────────────────────────
  probability?: IQuestProbability;
  branchRewards?: IQuestBranchRewards;
  // ── 로그 게시판 ──────────────────────────────────────────────────
  enableQuestLog?: boolean; // undefined/true → 로그 활성화 (기본 ON), false → 비활성화
  // ── 반복 조건 V2 ─────────────────────────────────────────────────
  repeatScheduleV2?: IQuestRepeatScheduleV2;
}

// 사용자별 퀘스트 상태 인터페이스
export interface IUserQuest {
  id: string;
  userId: string;
  missionId: string;
  status: QuestStatus;
  startedAt?: Date;
  completedAt?: Date;
  cancelledAt?: Date;
  progress?: number; // 0-100 진행률
  notes?: string; // 사용자 메모
  // ── 반복 퀘스트 전용 ──────────────────────────────────────────────
  /**
   * 반복 퀘스트 누적 수행 횟수. 비반복 퀘스트에서는 사용하지 않는다.
   */
  executionCount?: number;
  /**
   * 반복 퀘스트의 마지막 수행 시각(서버 기준).
   * `frequencyLimit.days === 1` 처럼 일일 주기 검증에 사용한다.
   */
  lastExecutedAt?: Date;
  /**
   * `frequencyLimit.days` 윈도우 내 수행 시각 목록(ms epoch).
   * 수행 시 범위 밖 이력은 제거하여 O(maxCount) 탐색을 유지한다.
   */
  recentExecutions?: number[];
}

// 퀘스트 로그 댓글 인터페이스
export interface IQuestComment {
  id: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  content: string;
  createdAt: Date;
  updatedAt?: Date;
  isDeleted?: boolean;
}

// 퀘스트 로그 좋아요 인터페이스
export interface IQuestLogLike {
  id: string;
  logId: string;
  userId: string;
  createdAt: Date;
}

// 퀘스트 필터 타입
export type QuestFilterType = 'all' | QuestType;

// 로그 타임라인 타입
export type LogTimelineType = 'all' | 'mission';

// 퀘스트 정렬 옵션
export enum QuestSortOption {
  PRIORITY = 'priority',
  CREATED_DATE = 'created_date',
  DIFFICULTY = 'difficulty',
  STATUS = 'status',
}

// 퀘스트 검색 필터
export interface IQuestSearchFilter {
  type?: QuestFilterType;
  status?: QuestStatus;
  difficulty?: number;
  sortBy?: QuestSortOption;
  sortOrder?: 'asc' | 'desc';
  searchText?: string;
}

// 퀘스트 생성/수정을 위한 입력 타입
export interface IQuestInput {
  type: QuestType;
  title: string;
  subTitle?: string;
  description: string;
  location?: string;
  requirements?: string[];
  rewards: Omit<IQuestReward, 'itemId'>[];
  difficulty: number;
  priority: number;
}

// 퀘스트 로그 생성을 위한 입력 타입
export interface IQuestLogInput {
  missionId: string;
  content: string;
  images?: File[]; // 업로드할 이미지 파일들
  hashtags?: string[];
  location?: string;
}

// 퀘스트 액션 타입
export enum QuestActionType {
  ACCEPT = 'accept',
  CANCEL = 'cancel',
  COMPLETE = 'complete',
  RESTART = 'restart',
}

// 퀘스트 완료 조건 타입
export enum QuestCompletionType {
  ITEM_OWNERSHIP = 'item_ownership', // 특정 아이템 보유 시
  STAT_VALUE = 'stat_value', // 특정 스탯 수치 보유 시
  LOG_COUNT = 'log_count', // 특정 로그 개수 업로드 완료 시
}

// 퀘스트 수락(진행) 조건 타입
export enum QuestAcceptConditionType {
  NONE = 'none', // 조건 없음
  STAT = 'stat', // 스탯 수치 조건
  ITEM = 'item', // 아이템 보유 조건
}

// 퀘스트 수락 스탯 조건 인터페이스
export interface IQuestAcceptStatCondition {
  statId: string; // 스탯 ID
  minValue: number; // 최소 요구 수치
}

// 퀘스트 수락 아이템 조건 인터페이스
export interface IQuestAcceptItemCondition {
  itemId: string; // 아이템 ID
  quantity: number; // 필요 수량
}

// 퀘스트 수락(진행) 조건 인터페이스
export interface IQuestAcceptCondition {
  type: QuestAcceptConditionType;
  // 스탯 조건 (type === 'stat' 일 때)
  statConditions?: IQuestAcceptStatCondition[];
  // 아이템 조건 (type === 'item' 일 때)
  itemConditions?: IQuestAcceptItemCondition[];
  // 조건 미충족 시 표시할 메시지
  failMessage?: string;
}

// 로그 랜덤 보상 아이템 인터페이스
export interface ILogRandomReward {
  id: string; // 고유 ID
  message: string; // 출력할 지문 메시지
  itemId?: string; // 지급할 아이템 ID (선택)
  itemAmount?: number; // 지급할 아이템 수량 (기본 1)
}

// 퀘스트 완료 조건 인터페이스
export interface IQuestCompletionCondition {
  type: QuestCompletionType;
  // 아이템 보유 조건
  itemIds?: string[]; // 보유해야 할 아이템 ID 목록
  itemAmounts?: Record<string, number>; // 아이템별 필요 수량 (기본 1)
  // 스탯 수치 조건
  statConditions?: {
    statId: string; // 스탯 ID
    minValue: number; // 최소 필요 수치
  }[];
  // 로그 개수 조건
  logCount?: number; // 업로드해야 할 로그 개수
  // 로그 업로드 시 랜덤 보상 설정 (LOG_COUNT 타입에서만 사용)
  logRandomRewards?: ILogRandomReward[]; // 랜덤 보상 목록
  enableLogRandomReward?: boolean; // 랜덤 보상 활성화 여부
}

export interface IQuestAction {
  missionId: string;
  actionType: QuestActionType;
  notes?: string;
}

// 퀘스트 반복 조건 인터페이스
export interface IQuestRepeatSchedule {
  enabled: boolean;
  daysOfWeek: number[]; // 0=일, 1=월, 2=화, 3=수, 4=목, 5=금, 6=토
}

export const defaultQuestRepeatSchedule: IQuestRepeatSchedule = {
  enabled: false,
  daysOfWeek: [1, 2, 3, 4, 5],
};

// ── 확률 시스템 ───────────────────────────────────────────────────────────────

/** 퀘스트 확률 시스템 설정 */
export interface IQuestProbability {
  enabled: boolean;
  baseSuccessRate: number; // 0~100 (관리자 직접 입력)
  influenceStat?: {
    type: 'none' | 'stat';
    statId?: string; // 영향을 미치는 스탯 ID
    weight?: number; // 스탯 1당 성공률 보정치 (기본 1)
    maxBonus?: number; // 최대 보정 상한 % (기본 30)
  };
}

/** 성공/실패 분기 보상 */
export interface IQuestBranchRewards {
  successRewards: IQuestReward[]; // 성공 시 보상
  failureRewards: IQuestReward[]; // 실패 시 보상 (빈 배열 = 보상 없음)
}

/**
 * 타임라인 게시글에 부착되는 퀘스트 1회 시도 결과 스냅샷.
 * - 결과 산출 시점에 동결되어 중복 지급/이중 롤을 방지한다.
 * - 조건 미충족 시에는 본 객체 자체를 저장하지 않는다.
 */
export interface IQuestExecutionResult {
  /** 시도된 퀘스트 ID */
  questId: string;
  /** 진행/완료 조건 충족 여부 (저장 시 항상 true) */
  conditionsSatisfied: boolean;
  /** 확률 비활성 시 'success' 고정, 활성 시 롤 결과 */
  probabilityResult: 'success' | 'failure';
  /** 표시용 최종 성공률 (calcSuccessRate 결과). 확률 비활성 시 100 */
  successRate?: number;
  /** 산출 시점 분기 보상 스냅샷 (수령 시 그대로 지급) */
  rewardsSnapshot: IQuestReward[];
  /** 수령 여부 */
  claimed: boolean;
  /** 수령 시각 (ms epoch) */
  claimedAt?: number;
  /** 결과 산출 시각 (ms epoch) */
  rolledAt: number;
}

/**
 * 확률 판정 헬퍼: 최종 성공률 계산
 * statValue가 없으면 influenceStat.type === 'none'으로 처리
 */
export function calcSuccessRate(
  probability: IQuestProbability,
  statValue?: number,
): number {
  if (!probability.enabled) return 100;
  const base = probability.baseSuccessRate;
  const inf = probability.influenceStat;
  if (!inf || inf.type === 'none' || statValue == null) return base;
  const weight = inf.weight ?? 1;
  const maxBonus = inf.maxBonus ?? 30;
  const bonus = Math.min(statValue * weight, maxBonus);
  return Math.max(0, Math.min(100, base + bonus));
}

/**
 * 확률 판정 실행: true = 성공, false = 실패
 */
export function rollQuestProbability(
  probability: IQuestProbability,
  statValue?: number,
): boolean {
  const rate = calcSuccessRate(probability, statValue);
  return Math.random() * 100 < rate;
}

// ── 반복 조건 V2 ──────────────────────────────────────────────────────────────

/** 반복 조건 V2: 기간 + 횟수 + 요일 통합 */
export interface IQuestRepeatScheduleV2 {
  enabled: boolean;
  /** 퀘스트 활성 기간 (없으면 무기한) */
  dateRange?: {
    startDate: string; // 'YYYY-MM-DD'
    endDate: string;   // 'YYYY-MM-DD'
  };
  /** N일 내 최대 M번 수행 제한 (없으면 무제한) */
  frequencyLimit?: {
    days: number;     // 기준 일수 (ex: 7 = 7일 내)
    maxCount: number; // 최대 수행 횟수
  };
  /** 수행 가능한 요일 제한 (빈 배열 = 제한 없음, 0=일~6=토) */
  daysOfWeek?: number[];
}

/** V1 → V2 런타임 변환 헬퍼 (저장하지 않고 읽기 전용) */
export function toRepeatScheduleV2(
  v1: IQuestRepeatSchedule | null | undefined,
): IQuestRepeatScheduleV2 | undefined {
  if (!v1) return undefined;
  return {
    enabled: v1.enabled,
    dateRange: undefined,
    frequencyLimit: undefined,
    daysOfWeek: v1.daysOfWeek ?? [],
  };
}

// ── 게시판 레벨 설정 ──────────────────────────────────────────────────────────

/** 퀘스트 게시판 전체 설정 (setting/quest 문서에 저장) */
export interface IQuestBoardSetting {
  /** 타임라인 연동 설정 */
  timelineIntegration?: {
    enabled: boolean;
    /** 연동할 타임라인 게시판 path (예: 'timeline') */
    timelineBoardPath?: string;
    /** 타임라인 게시글을 로그 카운트에 포함할지 여부 (옵션 B 기본값 false) */
    logCountFromTimeline?: boolean;
  };
  /** 커스텀 용어 설정 */
  customTerms?: {
    /** '퀘스트' 대체 단어 (기본: '퀘스트') */
    questLabel?: string;
  };
}

/** '퀘스트' 라벨 반환 (커스텀 용어 적용) */
export function getQuestLabel(setting?: IQuestBoardSetting | null): string {
  return setting?.customTerms?.questLabel?.trim() || '퀘스트';
}

// 퀘스트 히스토리 액션 타입
export enum QuestHistoryAction {
  ACCEPT = 'accept', // 퀘스트 수락
  CANCEL = 'cancel', // 퀘스트 포기
  COMPLETE = 'complete', // 퀘스트 완료 (보상 수령)
  RESTART = 'restart', // 퀘스트 다시 시작
  ADMIN_FORCE_COMPLETE = 'admin_force_complete', // 관리자 강제 완료
  ADMIN_FORCE_CANCEL = 'admin_force_cancel', // 관리자 강제 취소(포기)
  ADMIN_FORCE_RESET = 'admin_force_reset', // 관리자 강제 진행 중으로 복원
  ADMIN_FORCE_ADD = 'admin_force_add', // 관리자 강제 퀘스트 추가
}

// 퀘스트 히스토리 로그 인터페이스
export interface IQuestHistory {
  id: string;
  questId: string; // 퀘스트 ID
  questTitle: string; // 퀘스트 제목
  action: QuestHistoryAction; // 수행한 액션
  characterId: string; // 캐릭터 ID
  characterName?: string; // 캐릭터 이름
  timestamp: Date; // 액션 수행 시간
  notes?: string; // 추가 메모 (보상 정보 등)
}
